import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppThunk } from "@Store/index";
import { getMetaSuccess } from "@App/appReducer";
import type { EditorialPageData } from "../types";
import getEditorialPageData from "./getEditorialPageData";
import getEditorialPageSchema from "./editorialPageSchema";
import {
  LEGACY_EDITORIAL_SLUG,
  resolveEditorialCanonicalPath,
  resolveEditorialCanonicalSuffix,
} from "../utils/editorialPaths";

export interface EditorialPageState {
  readyStatus: "invalid" | "request" | "success" | "failure";
  data: EditorialPageData | null;
}

const initialState: EditorialPageState = {
  readyStatus: "invalid",
  data: null,
};

const editorialPageSlice = createSlice({
  name: "editorialPageData",
  initialState,
  reducers: {
    setRequest: (state) => {
      state.readyStatus = "request";
    },
    setSuccess: (state, { payload }: PayloadAction<EditorialPageData>) => {
      state.readyStatus = "success";
      state.data = payload;
    },
    setFailure: (state) => {
      state.readyStatus = "failure";
      state.data = null;
    },
  },
});

const { setRequest, setSuccess, setFailure } = editorialPageSlice.actions;
export default editorialPageSlice.reducer;

const getRequestPath = (props: any): string =>
  __SERVER__ ? props?.req?.path || "" : window.location.pathname;

export const fetchEditorialPageData =
  (props?: any): AppThunk =>
  async (dispatch, getState) => {
    if ((getState() as any).editorialPageData?.readyStatus === "success") return;

    dispatch(setRequest());

    try {
      const data = await getEditorialPageData();
      const postBody = data?.postBody as { root?: unknown } | null | undefined;
      if (!data?.hero || !postBody?.root) {
        if (props?.staticContext) props.staticContext.statusCode = 404;
        dispatch(setFailure());
        return;
      }

      const lang = (getState() as any).appData?.userLanguage as string | undefined;
      const langPrefix = lang && lang !== "en" ? lang : undefined;
      const canonicalSuffix = resolveEditorialCanonicalSuffix(data, langPrefix);
      const canonicalPath = resolveEditorialCanonicalPath(data);

      const path = getRequestPath(props);
      const slugSegment = path.split("/").filter(Boolean).pop() || "";
      const isLegacyPath =
        slugSegment === LEGACY_EDITORIAL_SLUG || path.includes(`/${LEGACY_EDITORIAL_SLUG}`);

      if (isLegacyPath) {
        let query = "";
        if (props?.req?.originalUrl?.includes("?")) {
          query = props.req.originalUrl.slice(props.req.originalUrl.indexOf("?"));
        } else if (typeof window !== "undefined") {
          query = window.location.search;
        }
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        const target = `${langPrefix ? `/${langPrefix}` : ""}${canonicalPath}${query}${hash}`;
        if (props?.staticContext) {
          props.staticContext.url = target;
          return;
        }
        if (typeof window !== "undefined" && !path.endsWith(canonicalPath)) {
          window.location.replace(target);
          return;
        }
      }

      dispatch(
        getMetaSuccess({
          title: data.metaTitle,
          description: data.metaDescription,
          canonical_url_suffix: canonicalSuffix,
          ogTitle: data.ogTitle || data.metaTitle,
          ogDescription: data.ogDescription || data.metaDescription,
          ogImage: data.ogImageUrl || undefined,
          index: data.robotsIndex !== false,
          schemaData: getEditorialPageSchema(data, langPrefix),
          excludeSiteSchema: true,
        } as any),
      );
      dispatch(setSuccess(data));
    } catch (error: any) {
      if (props?.staticContext) {
        props.staticContext.statusCode = error?.response?.status === 404 ? 404 : 500;
      }
      dispatch(setFailure());
    }
  };
